import { pointer } from "d3";
import { ScaleLinear } from "d3-scale";
import { select, Selection } from "d3-selection";
import { transform } from "../../../utils/transform";
import { ChartMargin } from "../interfaces/chart-margin";
import {
    LineChartXDomain,
    LineChartTooltipFromatterInputData,
} from "./line-chart";
import { LineChartSvgChartArea } from "./line-chart-svg-chart-area";

export class LineChartSvg {
    selection: Selection<SVGElement, unknown, HTMLElement, any>;
    chartArea: LineChartSvgChartArea;
    constructor(id, parentWidth, parentHeight) {
        this.selection = select(`#${id}`)
            .append("svg")
            .attr("width", parentWidth)
            .attr("height", parentHeight);
    }

    public show() {
        this.selection.style("visibility", "inherit");
        return this;
    }

    public hide() {
        this.selection.style("visibility", "hidden");
        return this;
    }

    public appendYAxisLabelArea() {
        return this.selection
            .append("g")
            .attr("class", "uic-line-chart__y-axis-label");
    }

    public appendChartArea(margin: ChartMargin): LineChartSvgChartArea {
        this.chartArea = new LineChartSvgChartArea(this.selection, margin);
        return this.chartArea;
    }

    public updateDimensions(
        parentWidth: number,
        parentHeight: number,
        transitionDurationMs: number
    ) {
        this.selection
            .transition()
            .duration(transitionDurationMs)
            .attr("width", parentWidth)
            .attr("height", parentHeight);

        return this;
    }

    public updateYAxisLabel(
        yAxisLabelData: string[],
        margin: ChartMargin,
        parentHeight: number,
        transitionDurationMs: number
    ) {
        const yAxisLabel = this.selection
            .select(".uic-line-chart__y-axis-label")
            .selectAll("text")
            .data(yAxisLabelData);
        yAxisLabel
            .enter()
            .append("text")
            .attr("text-anchor", "middle")
            .attr("y", margin.left)
            .attr("x", -parentHeight / 2)
            .attr("transform", "rotate(-90)")
            .attr("dy", "1em")
            .text((d) => d);

        yAxisLabel
            .text((d) => d)
            .transition()
            .duration(transitionDurationMs)
            .attr("y", margin.left)
            .attr("x", -parentHeight / 2);

        yAxisLabel.exit().remove();

        return this;
    }

    public addTooltipHandler(
        tooltipElement: HTMLUicTooltipElement,
        bisect: (mx: number) => LineChartTooltipFromatterInputData,
        xScale: any,
        yScale: ScaleLinear<number, number>,
        xOffset: number,
        yOffset: number,
        tooltipTextFormatter: (x: {
            key: LineChartXDomain;
            values: { [groupKey: string]: number };
        }) => string
    ) {
        if (!tooltipElement) {
            this.selection.on("touchmove mousemove touchstart", null);
            this.selection.on("touchend mouseleave", null);
            return this;
        }

        const tooltipLine = this.selection
            .selectAll(".uic-line-chart__tooltip-line")
            .data([null]) // only ever append one line
            .join("line")
            .classed("uic-line-chart__tooltip-line", true)
            .attr("display", "none");

        select(tooltipElement).on("touchend mouseleave", function () {
            tooltipElement.open = false;
            tooltipLine.style("display", "none");
        });

        this.selection.on("touchmove mousemove touchstart", function (
            event: Event
        ) {
            event.preventDefault();

            // reopen to trigger popperjs placement
            tooltipElement.open = false;
            tooltipElement.open = true;

            const pointerEvent =
                window.TouchEvent && event instanceof TouchEvent
                    ? event.touches[0]
                    : event;
            const dataPoint = bisect(pointer(pointerEvent, this)[0]);
            const y = Math.max(...Object.values(dataPoint.values));
            const [yMin] = yScale.range();
            const x = xScale(dataPoint.key) + xOffset;

            select(tooltipElement)
                .style("position", "absolute")
                .style("display", "block")
                .style("left", x + "px")
                .style("top", yScale(y) + yOffset + "px")
                .select('[slot="tip"]')
                .html(transform(tooltipTextFormatter, dataPoint));

            tooltipLine
                .style("display", "initial")
                .attr("x1", x + "px")
                .attr("x2", x + "px")
                .attr("y1", yMin + yOffset)
                .attr("y2", yScale(y) + yOffset);
        });
        this.selection.on("touchend mouseleave", function (event: MouseEvent) {
            if (
                event.relatedTarget &&
                (event.relatedTarget as HTMLElement).closest("uic-tooltip")
            ) {
                return;
            }

            tooltipElement.open = false;
            tooltipLine.style("display", "none");
        });

        return this;
    }
}
